import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signup as apiSignup } from "../api";
import { Check, Circle, Eye, EyeOff, X } from "lucide-react";

import logoNoBg from "../assets/logonobg.png";

// ─── Terms & Conditions Modal ────────────────────────────────────────────────
function TermsModal({ onClose, onAgree }) {
  const [error, setError] = useState('');
  const modalStyles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
      backgroundColor: '#fff', borderRadius: '12px',
      width: '540px', maxWidth: '95vw', maxHeight: '90vh',
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
    },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
    },
    title: { fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0, marginBottom: '4px' },
    subtitle: { fontSize: '12px', color: '#6b7280', margin: 0 },
    closeBtn: {
      background: 'none', border: 'none', cursor: 'pointer',
      color: '#6b7280', padding: '4px', borderRadius: '4px',
      display: 'flex', alignItems: 'center',
    },
    body: { overflowY: 'auto', padding: '20px 24px', flex: 1 },
    footer: { padding: '16px 24px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb' },
    checkLabel: {
      display: 'flex', alignItems: 'center', gap: '10px',
      marginBottom: '14px', cursor: 'pointer',
      backgroundColor: '#f0fdf4', padding: '10px 12px',
      borderRadius: '8px', border: '1px solid #bbf7d0',
    },
    customCheckbox: {
      width: '18px', height: '18px', minWidth: '18px',
      border: '2px solid #9ca3af', borderRadius: '4px',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s',
    },
    footerBtns: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
    declineBtn: {
      padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '8px',
      backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px', color: '#374151',
    },
    agreeBtn: {
      padding: '10px 20px', backgroundColor: '#14532d', border: 'none',
      borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
    },
  };
  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <div>
            <h2 style={modalStyles.title}>Terms & Conditions and Privacy Policy</h2>
            <p style={modalStyles.subtitle}>Please read and agree before continuing</p>
          </div>
          <button style={modalStyles.closeBtn} onClick={onClose}><X size={16} /></button>
        </div>

        <div style={modalStyles.body}>
          <Section number="1" title="Agreement">
            <p>By creating an account or logging in to Ukit Kapampangan, you confirm that you have read and agree to these Terms &amp; Conditions and Privacy Policy.</p>
            <p>You must be at least 18 years old to use this platform.</p>
          </Section>
          <Section number="2" title="Account Responsibility">
            <p>You are responsible for:</p>
            <BulletList items={["Keeping your login credentials secure", "All activities made under your account", "Providing accurate and complete information"]} />
            <p>Do not share your account with others.</p>
          </Section>
          <Section number="3" title="Use of the Platform">
            <p>Ukit Kapampangan is an online platform for browsing, customizing, and ordering furniture. By using this platform, you agree to:</p>
            <BulletList items={["Use the system for lawful purposes only", "Not attempt to hack, disrupt, or damage the system", "Not submit fake orders or false payment information"]} />
          </Section>
          <Section number="4" title="Orders and Payments">
            <BulletList items={["All orders made using your account are considered valid", "Customized furniture requests are subject to approval", "Payments must be completed before order processing", "Prices and availability may change without prior notice"]} />
          </Section>
          <Section number="5" title="Data Privacy">
            <p>We collect personal information such as:</p>
            <BulletList items={["Name, email, phone number", "Delivery address", "Order and payment details"]} />
            <p>Your data is used for:</p>
            <BulletList items={["Account management", "Order processing and delivery", "System security"]} />
            <p>We do not sell your personal data. Some data may be processed by trusted services such as payment gateways, maps, and cloud storage.</p>
          </Section>
          <Section number="6" title="Security">
            <p>We implement security measures such as:</p>
            <BulletList items={["Encrypted passwords", "Secure data transmission"]} />
            <p>However, no system is 100% secure. Users are advised to protect their own accounts.</p>
          </Section>
          <Section number="7" title="User Rights (RA 10173)">
            <p>Under the Data Privacy Act of 2012, you have the right to:</p>
            <BulletList items={["Access your personal data", "Update or correct your information", "Request account deletion"]} />
          </Section>
          <Section number="8" title="Account Suspension">
            <p>Your account may be suspended or terminated if you:</p>
            <BulletList items={["Violate these terms", "Engage in fraudulent or harmful activities"]} />
          </Section>
          <Section number="9" title="Disclaimer">
            <p>This system is an academic capstone project and is provided for demonstration purposes only. The developers are not liable for any data loss or misuse beyond reasonable control.</p>
          </Section>
          <Section number="10" title="Updates">
            <p>These terms may be updated at any time. Continued use of the platform means you accept the changes.</p>
          </Section>
          <Section number="11" title="Contact">
            <p>📧 <a href="mailto:ukitkapampangan@gmail.com" style={{ color: '#0f3d3e' }}>ukitkapampangan@gmail.com</a></p>
          </Section>
        </div>

        <div style={modalStyles.footer}>
          <label style={modalStyles.checkLabel}>
            <div
              id="modal-checkbox-box"
              style={modalStyles.customCheckbox}
              onClick={() => {
                const box = document.getElementById('modal-checkbox-box');
                const isChecked = box.dataset.checked === 'true';
                box.dataset.checked = (!isChecked).toString();
                box.style.backgroundColor = !isChecked ? '#14532d' : '#fff';
                box.style.borderColor = !isChecked ? '#14532d' : '#9ca3af';
                box.innerHTML = !isChecked
                  ? `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>`
                  : '';
              }}
              data-checked="false"
            />
             <span style={{ fontSize: '13px' }}>
              I have read and agree to the{' '}
              <span style={{ color: '#0f3d3e', fontWeight: '600' }}>Terms &amp; Conditions</span>
              {' '}and{' '}
              <span style={{ color: '#0f3d3e', fontWeight: '600' }}>Privacy Policy</span>
            </span>
          </label>
          {error && <div style={{ color: '#c0392b', fontSize: '12px', marginBottom: '12px', textAlign: 'center', fontWeight: '500' }}>{error}</div>}
          <div style={modalStyles.footerBtns}>
            <button style={modalStyles.declineBtn} onClick={onClose}>Decline</button>
            <button
              style={modalStyles.agreeBtn}
              onClick={() => {
                const box = document.getElementById('modal-checkbox-box');
                if (box.dataset.checked !== 'true') {
                  setError('Please check the box to confirm you have read and agree to the Terms & Conditions and Privacy Policy.');
                  return;
                }
                onAgree();
              }}
            >
              Agree and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' }}>
        {number}. {title}
      </h3>
      <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{children}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ margin: '6px 0 6px 4px', paddingLeft: '0', listStyle: 'none' }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: 'flex', gap: '8px', marginBottom: '4px', alignItems: 'flex-start' }}>
          <span style={{ color: '#6b7280', marginTop: '2px', fontSize: '10px' }}>■</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SignupForm() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      margin: 0,
      padding: 0,
    },
    topHeader: {
      backgroundColor: '#14291F',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    logo: {
      height: '100%',
      maxHeight: windowWidth < 768 ? '40px' : '55px',
      objectFit: 'contain'
    },
    content: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: windowWidth < 768 ? '30px 16px' : '60px 0',
    },
    card: {
      width: windowWidth < 768 ? '100%' : '420px',
      maxWidth: '420px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #d1d5db',
      padding: windowWidth < 768 ? '24px 20px' : '32px',
      textAlign: 'center'
    },
    titleMain: {
      fontFamily: 'Abril Fatface, serif',
      fontSize: '28px',
      color: '#2E4B4C',
      margin: '0 0 10px 0'
    },
    subtitle: {
      fontSize: '13px',
      color: '#6b7280',
      marginBottom: '20px'
    },
    nameRow: {
      display: 'flex',
      flexDirection: windowWidth < 480 ? 'column' : 'row',
      gap: windowWidth < 480 ? '0' : '12px',
      marginBottom: windowWidth < 480 ? '0' : '10px'
    },
    nameCol: {
      flex: 1
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #9ca3af',
      marginBottom: '8px',
      boxSizing: 'border-box',
      fontSize: '14px'
    },
    passwordWrapper: {
      position: 'relative',
      width: '100%'
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      top: '22px',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#9ca3af',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4px',
      borderRadius: '40px'
    },
    button: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#0f3d3e',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginTop: '10px'
    },
    error: {
      color: 'red',
      fontSize: '13px',
      marginBottom: '10px'
    },
    errorText: {
      color: '#c0392b',
      fontSize: '12px',
      marginTop: '-4px',
      marginBottom: '12px',
      textAlign: 'left'
    },
    checklistContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      marginBottom: '16px',
      padding: '0 4px'
    },
    checkItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    checkLabel: {
      fontSize: '12px',
      transition: 'all 0.2s'
    },
    signupText: {
      textAlign: 'center',
      marginTop: '17px',
      fontSize: '14px'
    },
    signupLink: {
      color: '#0f3d3e',
      textDecoration: 'none',
      fontWeight: 'bold',
      cursor: 'pointer'
    },
    termsRow: {
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      marginBottom: '12px', textAlign: 'left',
    },
    termsCheckbox: {
      marginTop: '1px',
      width: '18px', height: '18px', minWidth: '18px',
      border: '2px solid #9ca3af', borderRadius: '4px',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s', flexShrink: 0,
    },
    termsText: { fontSize: '13px', color: '#374151', lineHeight: '1.5', margin: 0 },
    termsLink: {
      background: 'none', border: 'none', padding: 0, margin: 0,
      color: '#0f3d3e', fontWeight: '600', cursor: 'pointer',
      fontSize: '13px', textDecoration: 'underline', display: 'inline',
    },
  };

  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
  });

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  // ✅ Real-time validation
  useEffect(() => {
    const newErrors = {};

    if (!firstName.trim()) newErrors.firstName = "First name is required.";
    if (!lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!email.trim()) newErrors.email = "Email is required.";
    else if (!isValidEmail(email))
      newErrors.email = "Enter a valid email address.";
    if (!password.trim()) newErrors.password = "Password is required.";
    else if (password.length < 8 || password.length > 20 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password))
      newErrors.password = "Password does not meet requirements.";
    if (!confirmPassword.trim()) newErrors.confirmPassword = "Please re-enter your password.";
    else if (password !== confirmPassword) newErrors.confirmPassword = "Passwords do not match.";

    setErrors(newErrors);
  }, [
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
  ]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setGeneralError("");
    setTermsError("");

    // Mark all fields as touched on submit
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      password: true,
      confirmPassword: true,
    });

    const hasFieldErrors = Object.values(errors).some(Boolean);
    if (!agreedToTerms) {
      setTermsError("You must agree to the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    if (hasFieldErrors) return;

    // Send data to backend via api.js
    setLoading(true);
    try {
      await apiSignup(firstName, lastName, email, password);
      // Success - redirect to verification page
      navigate(`/verify?email=${encodeURIComponent(email)}`);
    } catch (error) {
      console.error("Signup error:", error);
      
      // Check if error is about email domain or format
      if (error.message.includes('Invalid email') || error.message.includes('domain')) {
        setErrors({ ...errors, email: error.message });
      } else if (error.message.includes('already registered')) {
        const isVerified = error.data?.verified;
        if (isVerified === false) {
          setGeneralError(
            <div style={{ textAlign: 'center' }}>
              This email is already registered but not yet verified. <br />
              <button 
                type="button" 
                onClick={() => navigate(`/verify?email=${encodeURIComponent(email)}`)}
                style={{ 
                  background: 'none', border: 'none', padding: 0, 
                  color: '#14291F', fontWeight: 'bold', cursor: 'pointer',
                  textDecoration: 'underline', marginTop: '8px', fontSize: '14px'
                }}
              >
                Click here to verify your account
              </button>
            </div>
          );
        } else {
          setErrors({ ...errors, email: 'This email is already registered. Please use another email or log in.' });
        }
      } else if (error.message.includes('failed to send verification email')) {
        setGeneralError('Account created but verification email could not be sent. Please try again.');
      } else {
        setGeneralError(error.message || "Server error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirements = ({ value }) => {
    const checks = [
      { label: "8 to 20 characters", fulfilled: value.length >= 8 && value.length <= 20 },
      { label: "At least one uppercase letter", fulfilled: /[A-Z]/.test(value) },
      { label: "At least one lowercase letter", fulfilled: /[a-z]/.test(value) },
      { label: "At least one number", fulfilled: /[0-9]/.test(value) },
    ];

    return (
      <div style={styles.checklistContainer}>
        {checks.map((check, index) => (
          <div key={index} style={styles.checkItem}>
            {check.fulfilled ? (
              <Check size={14} color="#10b981" strokeWidth={3} />
            ) : (
              <Circle size={14} color="#9ca3af" />
            )}
            <span style={{
              ...styles.checkLabel,
              color: check.fulfilled ? "#10b981" : "#6b7280",
              fontWeight: check.fulfilled ? "600" : "400"
            }}>
              {check.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      {/* Terms Modal */}
      {showTermsModal && (
        <TermsModal
          onClose={() => setShowTermsModal(false)}
          onAgree={() => {
            setAgreedToTerms(true);
            setTermsError("");
            setShowTermsModal(false);
          }}
        />
      )}

      {/* TOP HEADER */}
      <div style={styles.topHeader}>
        <img
          src={logoNoBg}
          alt="Logo"
          style={styles.logo}
        />
      </div>

      {/* SIGNUP CARD */}
      <div style={styles.content}>
        <div style={styles.card}>
          <h1 style={styles.titleMain}>UKIT KAPAMPANGAN</h1>
          <p style={styles.subtitle}>Create your account</p>

          {generalError && <div style={styles.error}>{generalError}</div>}

          <form onSubmit={handleSignup} noValidate>
            {/* First Name and Last Name in one row */}
            <div style={styles.nameRow}>
              <div style={styles.nameCol}>
                <input
                  type="text"
                  placeholder="First Name"
                  autoComplete="off"
                  style={styles.input}
                  value={firstName}
                  onChange={(e) =>
                    setFirstName(e.target.value.replace(/[^a-zA-Z\s'-]/g, ""))
                  }
                  onBlur={() => handleBlur("firstName")}
                />
                {touched.firstName && errors.firstName && (
                  <div style={styles.errorText}>{errors.firstName}</div>
                )}
              </div>

              <div style={styles.nameCol}>
                <input
                  type="text"
                  placeholder="Last Name"
                  autoComplete="off"
                  style={styles.input}
                  value={lastName}
                  onChange={(e) =>
                    setLastName(e.target.value.replace(/[^a-zA-Z\s'-]/g, ""))
                  }
                  onBlur={() => handleBlur("lastName")}
                />
                {touched.lastName && errors.lastName && (
                  <div style={styles.errorText}>{errors.lastName}</div>
                )}
              </div>
            </div>

            <input
              type="email"
              placeholder="Email"
              autoComplete="off"
              style={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur("email")}
            />
            {touched.email && errors.email && (
              <div style={styles.errorText}>{errors.email}</div>
            )}

            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                autoComplete="new-password"
                style={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur("password")}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <PasswordRequirements value={password} />
            {touched.password && errors.password && (
              <div style={styles.errorText}>{errors.password}</div>
            )}

            <div style={styles.passwordWrapper}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Re-enter Password"
                autoComplete="new-password"
                style={styles.input}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => handleBlur("confirmPassword")}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {touched.confirmPassword && errors.confirmPassword && (
              <div style={styles.errorText}>{errors.confirmPassword}</div>
            )}

            {/* Terms & Conditions */}
            <div style={styles.termsRow}>
              <div
                style={{
                  ...styles.termsCheckbox,
                  backgroundColor: agreedToTerms ? '#14532d' : '#fff',
                  borderColor: agreedToTerms ? '#14532d' : termsError ? '#c0392b' : '#9ca3af',
                }}
                onClick={() => {
                  if (!agreedToTerms) {
                    setShowTermsModal(true);
                  } else {
                    setAgreedToTerms(false);
                  }
                }}
              >
                {agreedToTerms && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span style={styles.termsText}>
                I have read and agree to the{' '}
                <button type="button" style={styles.termsLink} onClick={() => setShowTermsModal(true)}>Terms &amp; Conditions</button>
                {' '}and{' '}
                <button type="button" style={styles.termsLink} onClick={() => setShowTermsModal(true)}>Privacy Policy</button>
              </span>
            </div>
            {termsError && <div style={{ ...styles.errorText, marginTop: '-6px', marginBottom: '10px' }}>{termsError}</div>}

            <button
              type="submit"
              style={styles.button}
              disabled={loading}
            >
              {loading ? "Signing Up..." : "Sign Up"}
            </button>
          </form>

          <p style={styles.signupText}>
            Already have an account?{" "}
            <Link
              to="/login"
              style={styles.signupLink}
            >
              Log In.
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}



export default SignupForm;